const THREE_URL = 'https://cdn.jsdelivr.net/npm/three@0.166.1/build/three.module.js';
const GLTF_LOADER_URL = 'https://cdn.jsdelivr.net/npm/three@0.166.1/examples/jsm/loaders/GLTFLoader.js';
const ORBIT_CONTROLS_URL = 'https://cdn.jsdelivr.net/npm/three@0.166.1/examples/jsm/controls/OrbitControls.js';

const initialise = async () => {
  const viewers = document.querySelectorAll('[data-ll-3d-viewer]:not([data-ready])');
  if (!viewers.length) return;

  const [THREE, { GLTFLoader }, { OrbitControls }] = await Promise.all([
    import(THREE_URL),
    import(GLTF_LOADER_URL),
    import(ORBIT_CONTROLS_URL)
  ]);

  viewers.forEach((viewer) => {
    viewer.dataset.ready = 'true';
    const canvas = viewer.querySelector('canvas');
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.08;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(34, 1, 0.1, 10000);
    const controls = new OrbitControls(camera, canvas);
    controls.enablePan = false;
	controls.enableZoom = false;
    controls.enableDamping = true;
    controls.dampingFactor = 0.07;
    controls.autoRotate = true;
    controls.autoRotateSpeed = 1.35;

    const heroLighting = viewer.dataset.presentation === 'hero';
    const fillLight = new THREE.HemisphereLight(0xf4f4f4, 0x050505, heroLighting ? 0.75 : 1.35);
    scene.add(fillLight);
    const keyLight = new THREE.DirectionalLight(0xffffff, heroLighting ? 0.45 : 1.35);
    keyLight.position.set(4, 6, 5);
    scene.add(keyLight);
    const rimLight = new THREE.DirectionalLight(heroLighting ? 0xf1f4ff : 0x8d97a4, heroLighting ? 7.5 : 0.65);
    rimLight.position.set(-5, 2, -4);
    scene.add(rimLight);
    if (heroLighting) {
      // Preserve dark faces; let the lattice cast real shadows under the key light.
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      renderer.shadowMap.autoUpdate = false;
      keyLight.castShadow = true;
      keyLight.shadow.mapSize.set(1024, 1024);
      keyLight.shadow.bias = -0.0001;
    }

    let largestDimension = 0;
    let isHero = false;

    const frameModel = () => {
      if (!largestDimension) return;

      // Pull the camera back on narrow screens so the complete object stays visible.
      const narrowScreenFit = isHero ? 1 + Math.max(0, (0.9 - camera.aspect) * 1.25) : 1;
      const cameraDistance = largestDimension * (isHero ? 1.65 : 1.45) * narrowScreenFit;
      const cameraHeight = largestDimension * (isHero ? 0.51 : 0.82) * narrowScreenFit;

      // Enlarge the desktop hero while preserving the mobile framing and other viewers.
      camera.zoom = isHero ? (window.matchMedia('(max-width: 749px)').matches ? 1.3 : 1.5) : 1;
      camera.updateProjectionMatrix();
      camera.position.set(cameraDistance, cameraHeight, cameraDistance);
      controls.minDistance = largestDimension * (isHero ? 1.05 : 0.8) * narrowScreenFit;
      controls.maxDistance = largestDimension * (isHero ? 4 : 3.5) * narrowScreenFit;
      controls.target.set(0, 0, 0);
      controls.update();
    };

    const resize = () => {
      // Gently lift desktop shadows without strengthening the rim or changing mobile.
      const desktopHero = heroLighting && !window.matchMedia('(max-width: 749px)').matches;
      fillLight.intensity = heroLighting ? (desktopHero ? 1.8 : 0.75) : 1.35;
      keyLight.intensity = heroLighting ? (desktopHero ? 0.8 : 0.45) : 1.35;
      const { width, height } = viewer.getBoundingClientRect();
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      frameModel();
    };

    new ResizeObserver(resize).observe(viewer);
    resize();

    new GLTFLoader().load(viewer.dataset.modelSrc, (gltf) => {
      const model = gltf.scene;
      const bounds = new THREE.Box3().setFromObject(model);
      const size = bounds.getSize(new THREE.Vector3());
      const center = bounds.getCenter(new THREE.Vector3());
      largestDimension = Math.max(size.x, size.y, size.z);
      isHero = viewer.dataset.presentation === 'hero';
      if (isHero) {
        keyLight.position.set(largestDimension * 1.2, largestDimension * 2, largestDimension * 0.5);
        const shadowCamera = keyLight.shadow.camera;
        shadowCamera.left = shadowCamera.bottom = -largestDimension;
        shadowCamera.right = shadowCamera.top = largestDimension;
        shadowCamera.near = largestDimension * 0.1;
        shadowCamera.far = largestDimension * 5;
        shadowCamera.updateProjectionMatrix();
        keyLight.shadow.normalBias = largestDimension * 0.0005;
      }

      model.position.sub(center);
      model.traverse((node) => {
		if (!node.isMesh) return;
		node.castShadow = node.receiveShadow = isHero;
		node.geometry.computeVertexNormals();
		node.material = new THREE.MeshStandardMaterial({ color: isHero ? 0x404040 : 0x3c3c3c, metalness: 0, roughness: 0.96 });
	  });
	  if (isHero && camera.aspect < 0.9) {
		model.position.y += largestDimension * (0.9 - camera.aspect) * 0.35;
	  }
      scene.add(model);
      if (isHero) renderer.shadowMap.needsUpdate = true;
      frameModel();
      viewer.classList.add('is-loaded');
    }, undefined, () => viewer.classList.add('has-error'));

    const render = () => {
      controls.update();
      if (isHero && largestDimension) {
        // OrbitControls rotates the camera, so keep the rim light behind the product
        // relative to the viewer instead of washing across the visible upper face.
        const orbitRadius = Math.hypot(camera.position.x, camera.position.z) || 1;
        rimLight.position.set(
          (-camera.position.x + camera.position.z * 0.6) / orbitRadius * largestDimension * 2,
          largestDimension * 0.7,
          (-camera.position.z - camera.position.x * 0.6) / orbitRadius * largestDimension * 2
        );
      }
      renderer.render(scene, camera);
      requestAnimationFrame(render);
    };
    render();
  });
};

initialise();
document.addEventListener('shopify:section:load', initialise);
